/**
 * The Exploration name card: the tag and the description.
 *
 * The exploration ID is composed rather than typed,
 * <Material>-<Alloy>-<LaserID>-<Tag>-<Date>: only the tag is entered here; the
 * substrate and the laser come from their cards and the date is the day the
 * exploration is started (helper/explorationName.js). The preview keeps
 * placeholders for the parts not chosen yet.
 */
import React, { useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  EXPLORATION_ID_TEMPLATE,
  EXPLORATION_TAG_PATTERN,
  previewExplorationName,
} from "../../../helper/explorationName";
import { dateStamp, substrateToken } from "../../../helper/selectionFlow";
import { MONO_FONT } from "./SetupCard";

export const TAG_FORMAT_HINT =
  "Free text, e.g. V1.0 or trial2. The ID is completed with the substrate, the laser and the start date.";
export const TAG_SPACE_ERROR = "The tag must not start or end with a space";

export default function NameCard({
  tag,
  description,
  materialName,
  laserName,
  onTagChange,
  onDescriptionChange,
}) {
  const [formatOpen, setFormatOpen] = useState(false);
  const tagError = tag && !EXPLORATION_TAG_PATTERN.test(tag) ? TAG_SPACE_ERROR : "";
  const preview = previewExplorationName(materialName, laserName, tag);

  return (
    <Stack spacing={2.25} sx={{ maxWidth: 720, width: "100%" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
        <TextField
          required
          id="explorationtag"
          label="Exploration tag"
          placeholder="e.g., V1.0"
          onChange={(event) => onTagChange(event.target.value)}
          value={tag}
          type="text"
          fullWidth
          autoComplete="off"
          error={!!tagError}
          helperText={tagError || TAG_FORMAT_HINT}
          inputProps={{ "data-testid": "exploration-tag-input" }}
          sx={{ maxWidth: { sm: 360 } }}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            px: 1.75,
            py: 1.25,
            borderRadius: 2,
            bgcolor: "#f3f5f9",
            border: "1px solid rgba(15, 23, 42, 0.06)",
          }}
        >
          <Typography
            data-testid="exploration-id-preview"
            sx={{
              fontFamily: MONO_FONT,
              fontSize: "0.85rem",
              fontWeight: 600,
              lineHeight: 1.5,
              overflowWrap: "anywhere",
              color: tag ? "#0f172a" : "text.secondary",
            }}
          >
            <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>Exploration ID: </Box>
            {preview}
          </Typography>
          <Button
            size="small"
            startIcon={<InfoOutlinedIcon />}
            onClick={() => setFormatOpen(true)}
            sx={{ mt: 0.5, ml: -0.75, textTransform: "none", fontWeight: 600 }}
          >
            Naming convention
          </Button>
        </Box>
      </Stack>

      <TextField
        required
        id="explorationdescription"
        label="Exploration Description"
        placeholder="What this run is for, what was changed since the last one"
        onChange={(event) => onDescriptionChange(event.target.value)}
        value={description}
        type="text"
        multiline
        rows={2}
        fullWidth
        helperText="Stored with the exploration"
        inputProps={{ "data-testid": "exploration-description-input" }}
      />

      <Dialog open={formatOpen} onClose={() => setFormatOpen(false)}>
        <DialogTitle>Naming Convention</DialogTitle>
        <DialogContent sx={{ marginTop: "2%", marginBottom: "2%" }}>
          <Typography variant="h6" gutterBottom>Format:</Typography>
          <Typography variant="body1" gutterBottom sx={{ fontFamily: MONO_FONT }}>
            {EXPLORATION_ID_TEMPLATE}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Only the tag is typed; the rest is filled in from the substrate and the laser
            chosen in their cards and the day the exploration is started.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Components:</Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Material & Alloy:</strong> the leading part of the chosen substrate name
            {materialName ? ` (${substrateToken(materialName)})` : " (e.g. SS-AISI304)"}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>LaserID:</strong> the chosen laser system
            {laserName ? ` (${laserName})` : " (e.g. IPG2020)"}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tag:</strong> free text of your choice (e.g. V1.0, trial2, dark_test)
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Date:</strong> the day the exploration is started, YYYYMMDD ({dateStamp()})
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Examples:</Typography>
          <Typography variant="body1" gutterBottom sx={{ fontFamily: MONO_FONT }}>SS-AISI304-IPG2020-V1.0-20250430</Typography>
          <Typography variant="body1" gutterBottom sx={{ fontFamily: MONO_FONT }}>AL-1-Carbide-CB3-40W-trial2-20260827</Typography>
          <Typography variant="body1" gutterBottom sx={{ fontFamily: MONO_FONT }}>CU-C110-YAG300-dark_test-20250620</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => setFormatOpen(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
